// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import useUploads from "@/hooks/useUploads";
import type { TyDriveFolder, TyDriveRecord, TyFile, TyFileCategory, TyStorageUsage } from "@/types";
import { formatBytes, readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import FileCard from "@/UI/cards/FileCard";
import FolderCard from "@/UI/cards/FolderCard";
import EmptyState from "@/UI/components/EmptyState";
import ActionsBar from "@/UI/components/ActionsBar";
import HeaderPanel from "@/UI/components/HeaderPanel";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import FilePreviewPopup from "@/UI/popups/FilePreviewPopup";
import RenamePopup from "@/UI/popups/RenamePopup";
import ReplaceFilePopup from "@/UI/popups/ReplaceFilePopup";
import UploadFilesPopup from "@/UI/popups/UploadFilesPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------

/**
 * The drive. Not a drive service: folders are derived from what each file belongs to, so there is
 * nothing to create, move or rename here. A person sees what exists, what it costs them, and can add
 * loose files, tag them, replace them or delete them.
 *
 * **Where you are lives in the URL**, not in component state. Walking into a folder is a navigation,
 * so the browser's back button and a phone's back gesture both walk back out of it, and a link to a
 * record's files is a link someone can send. Only going deeper is a history entry — see `go`.
 *
 * Deleting is owner and manager only, and permanent — there is no trash for files, because a trashed
 * file would still occupy the bucket and still count against the organization's storage.
 */
export default function DriveScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const navigate = useNavigate();
    const { isOwner, role } = usePermission();
    const addComp = usePopup((state) => state.addComp);
    const uploadsFinishedAt = useUploads((state) => state.finishedAt);
    const [params, setParams] = useSearchParams();

    const folder = params.get("folder");
    const record = params.get("record");
    const picked = (params.get("category") ?? "").split(",").filter(Boolean);
    const search = params.get("search") ?? "";

    /** The bar's filter mode edits a copy; only `apply` writes it to the URL. */
    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    const [draftPicked, setDraftPicked] = useState<string[]>(picked);

    const isFiltered = !!search || picked.length > 0;
    /**
     * A filter is asking for files, so it leaves the tree wherever it is applied — at the root it
     * means "every image in the drive", inside a folder it means "every image in here".
     */
    const isRoot = !folder && !isFiltered;
    /** A folder lists the records inside it. `others` has none — its files hang off nothing. */
    const isRecordLevel = !!folder && folder !== "others" && !record && !isFiltered;
    const isFileLevel = !isRoot && !isRecordLevel;

    const usage = useFetch<{ usage: TyStorageUsage }>("/drive/usage");
    const folders = useFetch<{ folders: TyDriveFolder[] }>("/drive/folders");
    /** The company's own files. One record, so they sit at the root rather than two clicks deep. */
    const rootFiles = useFetch<{ files: TyFile[]; total: number }>("/drive", {
        query: { ownerModel: "Organization", limit: constants.maxPageSize },
    });
    const records = useFetch<{ records: TyDriveRecord[] }>("/drive/folders", {}, false);
    const files = useFetch<{ files: TyFile[]; total: number }>("/drive", {}, false);
    const action = useFetch("/drive", {}, false);

    const canDelete = isOwner || role === "manager";

    /**
     * Only going **deeper** into the tree is a history entry — that is the step the back button and
     * the phone's back gesture have to undo, and nothing else in here creates one.
     *
     * Filters replace: narrowing a list is an action on what is already on screen, not a place.
     */
    const go = (next: Record<string, string | null>, isDeeper = false) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        setParams(merged, { replace: !isDeeper });
    };

    const loadFiles = () => {
        const query: Record<string, unknown> = { limit: constants.maxPageSize };
        if (folder) query.ownerModel = folder;
        if (record) query.ownerId = record;
        if (picked.length) query.category = picked.join(",");
        if (search.trim()) query.search = search.trim();
        files.invoke({ query });
    };

    // Everything on screen follows the URL, so one effect decides what to load.
    useEffect(() => {
        if (isRecordLevel) records.invoke({ query: { ownerModel: folder } });
        if (isFileLevel) loadFiles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folder, record, params.get("category"), search]);

    const refresh = () => {
        usage.invoke();
        folders.invoke();
        rootFiles.invoke({ query: { ownerModel: "Organization", limit: constants.maxPageSize } });
        if (isRecordLevel) records.invoke({ query: { ownerModel: folder } });
        if (isFileLevel) loadFiles();
    };

    useEffect(() => {
        if (!action.succeededAt) return;
        refresh();
        // Keyed on the success timestamp: a second action returning the same status must still refresh.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    // Uploads finish in the tray, which may well be on another screen by then. This is how the drive
    // hears about one that landed while the person was looking at it.
    useEffect(() => {
        if (!uploadsFinishedAt) return;
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadsFinishedAt]);

    /**
     * In place, with the rest of the folder behind the arrows — a new browser tab loses the gallery
     * and hands a signed URL to the address bar.
     */
    const openFile = (file: TyFile, list: TyFile[]) =>
        addComp({
            name: "file-preview",
            Component: FilePreviewPopup,
            anim: "fade",
            details: {
                files: list,
                index: Math.max(
                    0,
                    list.findIndex((item) => item._id === file._id)
                ),
            },
        });

    const openActions = (file: TyFile, anchor: DOMRect) => {
        const side = { place: "end", anim: "endSlide" } as const;
        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            {
                key: "rename",
                label: t.drive.actions.rename,
                icon: "edit",
                // One field and one keystroke's worth of thought: centred and quick, not a side form.
                onSelect: () =>
                    addComp({
                        name: "rename-file",
                        Component: RenamePopup,
                        anim: "scale",
                        details: { title: t.drive.renameTitle, value: file.name, path: `/drive/${file._id}`, onDone: refresh },
                    }),
            },
            {
                key: "replace",
                label: t.drive.actions.replace,
                icon: "refresh",
                onSelect: () => addComp({ name: "replace-file", Component: ReplaceFilePopup, ...side, details: { file } }),
            },
        ];

        if (canDelete) {
            actions.push({
                key: "remove",
                label: t.drive.actions.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.drive.actions.remove,
                            message: t.drive.confirmRemove.replace("{name}", file.name),
                            confirmLabel: t.drive.actions.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/drive/${file._id}` }),
                        },
                    }),
            });
        }

        addComp({
            name: "file-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    const openUpload = () =>
        addComp({
            name: "upload-files",
            Component: UploadFilesPopup,
            place: "end",
            anim: "endSlide",
            details: {
                // Uploading from inside a record's folder keeps the file in that record.
                owner: folder && folder !== "others" && record ? { model: folder, id: record } : null,
            },
        });

    const store = usage.data?.usage;
    const percent = store && store.allowed ? Math.min(100, Math.round((store.used / store.allowed) * 100)) : 0;
    const ownerLabel = (model: string) => t.drive.owners[model as keyof typeof t.drive.owners] ?? model;
    /** Read off the files themselves, so a record opened straight from a link still has a name. */
    const recordLabel = readLangValue(files.data?.files[0]?.owner.label, lang) || t.drive.unnamedRecord;

    /**
     * What this costs, and nothing else. No title: the app bar already says which section this is,
     * and a header repeating it is a line of furniture.
     */
    const head = (
        <header className="drive-screen__head">
            {/* The one number that decides whether anything else here can happen. */}
            {store && (
                <div className={`drive-screen__storage${percent >= 100 ? " drive-screen__storage--full" : ""}`}>
                    <div className="drive-screen__storage-head">
                        <span className="drive-screen__storage-label">
                            {store.isFree ? t.drive.freeQuota : t.drive.used}
                            {percent >= 100 && ` · ${t.drive.full}`}
                        </span>
                        <span className="drive-screen__storage-value">
                            {formatBytes(store.used, lang)} {t.drive.ofAllowed.replace("{allowed}", formatBytes(store.allowed, lang))}
                        </span>
                    </div>
                    <div className="drive-screen__bar">
                        <span className="drive-screen__bar-fill" style={{ width: `${percent}%` }} />
                    </div>
                    <div className="drive-screen__breakdown">
                        {store.byCategory
                            .filter((entry) => entry.size > 0)
                            .map((entry) => (
                                <span key={entry.category} className="drive-screen__breakdown-item">
                                    {t.drive.categories[entry.category]} {formatBytes(entry.size, lang)}
                                </span>
                            ))}
                    </div>
                </div>
            )}
        </header>
    );

    return (
        <section className="drive-screen">
            <HeaderPanel head={head}>
                <ActionsBar
                    addLabel={t.drive.upload}
                    onAdd={openUpload}
                    isFilterOpen={isFiltering}
                    onFilterOpen={() => {
                        // Seeded on open rather than kept in step by an effect — the draft only ever
                        // matters while the panel is up.
                        setDraftSearch(search);
                        setDraftPicked(picked);
                        setIsFiltering(true);
                    }}
                    hasFilters={isFiltered}
                    onFilterApply={() => {
                        setIsFiltering(false);
                        go({ search: draftSearch.trim() || null, category: draftPicked.join(",") || null });
                    }}
                    onFilterClear={() => {
                        setDraftSearch("");
                        setDraftPicked([]);
                        setIsFiltering(false);
                        go({ search: null, category: null });
                    }}
                    onFilterCancel={() => setIsFiltering(false)}
                    filterContent={
                        <>
                            <FilterSearchInput value={draftSearch} onChange={setDraftSearch} label={t.common.search} placeholder={t.drive.search} />
                            <MultiSelectInput
                                compact
                                value={draftPicked}
                                onChange={setDraftPicked}
                                options={constants.fileCategories.map((name) => ({ value: name, label: t.drive.categories[name] }))}
                                label={t.drive.allFiles}
                            />
                        </>
                    }
                />
            </HeaderPanel>

            {/* Where you are. Reading only — the back button is the one way up, so there is exactly
                one of them and the history cannot grow a second, faked entry. */}
            <nav className="drive-screen__path">
                {!isRoot && (
                    <button type="button" className="drive-screen__back" onClick={() => navigate(-1)} aria-label={t.common.back}>
                        <Icon name="chevron" size={16} />
                    </button>
                )}
                <span className="drive-screen__crumb">{t.drive.root}</span>
                {folder && (
                    <>
                        <span className="drive-screen__sep">/</span>
                        <span className="drive-screen__crumb">{ownerLabel(folder)}</span>
                    </>
                )}
                {record && (
                    <>
                        <span className="drive-screen__sep">/</span>
                        <span className="drive-screen__crumb drive-screen__crumb--current">{recordLabel}</span>
                    </>
                )}
                {picked.length > 0 && (
                    <>
                        <span className="drive-screen__sep">/</span>
                        <span className="drive-screen__crumb drive-screen__crumb--current">
                            {picked.map((name) => t.drive.categories[name as TyFileCategory]).join("، ")}
                        </span>
                    </>
                )}
                {search && (
                    <>
                        <span className="drive-screen__sep">/</span>
                        <span className="drive-screen__crumb drive-screen__crumb--current">{search}</span>
                    </>
                )}
            </nav>

            {action.error && <p className="drive-screen__state drive-screen__state--error">{action.error}</p>}

            {/* --- The root: folders first, then the company's own files ----------------------- */}
            {isRoot && (
                <>
                    {folders.loading && <p className="drive-screen__state">{t.common.loading}</p>}

                    {/* Nothing anywhere: no folder has a file in it and the company has none of its own. */}
                    {!folders.loading && !folders.data?.folders.length && !rootFiles.data?.files.length && (
                        <EmptyState icon="drive" title={t.drive.empty} description={t.drive.emptyHint} />
                    )}

                    <div className="drive-screen__grid">
                        {folders.data?.folders.map((item) => (
                            <FolderCard
                                key={item.ownerModel}
                                label={ownerLabel(item.ownerModel)}
                                count={item.count}
                                size={item.size}
                                onOpen={() => go({ folder: item.ownerModel, record: null }, true)}
                            />
                        ))}
                    </div>

                    {/* One company record, so its files sit here rather than two clicks deep. */}
                    {rootFiles.data?.files.length ? (
                        <>
                            <span className="drive-screen__divider" />
                            <div className="drive-screen__grid">
                                {rootFiles.data.files.map((file) => (
                                    <FileCard
                                        key={file._id}
                                        file={file}
                                        onOpen={() => openFile(file, rootFiles.data!.files)}
                                        onActions={(anchor) => openActions(file, anchor)}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}
                </>
            )}

            {/* --- The records inside a folder ------------------------------------------------ */}
            {isRecordLevel && (
                <>
                    {records.loading && <p className="drive-screen__state">{t.common.loading}</p>}
                    {!records.loading && !records.data?.records.length && <EmptyState icon="folder" title={t.drive.emptyFolder} />}

                    {records.data?.records.length ? (
                        <>
                            <div className="drive-screen__grid">
                                {records.data.records.map((item) => (
                                    <FolderCard
                                        key={item.ownerId ?? "loose"}
                                        label={readLangValue(item.label, lang) || t.drive.unnamedRecord}
                                        count={item.count}
                                        size={item.size}
                                        onOpen={() => go({ folder, record: String(item.ownerId) }, true)}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}
                </>
            )}

            {/* --- The files ------------------------------------------------------------------ */}
            {isFileLevel && (
                <>
                    {files.loading && <p className="drive-screen__state">{t.common.loading}</p>}
                    {files.error && <p className="drive-screen__state drive-screen__state--error">{files.error}</p>}
                    {!files.loading && !files.error && !files.data?.files.length && <EmptyState icon="drive" title={t.drive.empty} />}

                    {files.data?.files.length ? (
                        <>
                            <div className="drive-screen__grid">
                                {files.data.files.map((file) => (
                                    <FileCard
                                        key={file._id}
                                        file={file}
                                        onOpen={() => openFile(file, files.data!.files)}
                                        onActions={(anchor) => openActions(file, anchor)}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}
                </>
            )}
        </section>
    );
}
